import Button from '@carvana/ds-button';
import { useContext, useEffect } from 'react';
import DsModal from '@/common/DsModal';
import SectionLevelLabels from '~/constants/new-cars/SectionLevelLabels';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import type { AutoSelectConfirm } from '~/contracts/new-cars';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import { formatItemLabel } from './autoSelectFormatters';

type OwnProps = {
  confirm: AutoSelectConfirm;
  isOpen: boolean;
  onCancel: () => void;
  onContinue: () => void;
};

export default function AutoSelectModal({
  confirm,
  isOpen,
  onCancel,
  onContinue
}: OwnProps) {
  const { activeSection } = useContext(ParentModelContext);
  const trackEvent = useTrackEvent();
  const level = SectionLevelLabels[activeSection];
  const { additions, confirmType, removals, trigger } = confirm;
  const additionLabels = additions
    .map(item => formatItemLabel(item.key, item.name))
    .join(', ');
  const removalLabels = removals
    .map(item => formatItemLabel(item.key, item.name))
    .join(', ');

  useEffect(() => {
    if (!isOpen) return;

    trackEvent('New Cars', {
      action: 'viewed',
      additions: additionLabels,
      level,
      removals: removalLabels,
      section: 'Auto Select Modal'
    });
  }, [additionLabels, isOpen, level, removalLabels, trackEvent]);

  const isOnlyRemovals = removals.length > 0 && additions.length === 0;
  const triggerLabel = trigger?.name ?? 'This Selection';

  return (
    <DsModal
      classes={{ container: 'max-w-[440px]' }}
      isOpen={isOpen}
      modalHeader=""
      mobileStyle="inset"
      onClose={onCancel}
    >
      <div className="flex flex-col gap-2xl max-h-[70vh]">
        <strong className="t-strong-lg">
          {confirmType === 'deselect'
            ? `Removing ${triggerLabel}`
            : triggerLabel}
        </strong>
        <div className="flex flex-col gap-xl overflow-y-auto min-h-0">
          {additions.length > 0 && (
            <div className="flex flex-col gap-sm">
              <p className="t-label-sm text-text-subtle">
                is currently only available with:
              </p>
              <ul className="flex flex-col gap-sm t-body-sm list-disc list-outside pl-2xl text-text-default">
                {additions.map(item => (
                  <li key={item.code}>
                    <span>{formatItemLabel(item.key, item.name)}</span>{' '}
                    <span>
                      (
                      <span>
                        {item.priceAdd > 0
                          ? `+$${formatAndDropDecimal(item.priceAdd)}`
                          : '+$0'}
                      </span>
                      )
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {removals.length > 0 && (
            <div className="flex flex-col gap-sm">
              <p className="t-label-sm text-text-subtle">
                {isOnlyRemovals
                  ? 'is currently unavailable with:'
                  : 'and unavailable with:'}
              </p>
              <ul className="flex flex-col gap-sm t-body-sm list-disc list-outside pl-2xl text-text-default">
                {removals.map(item => (
                  <li key={item.code}>
                    <span>{formatItemLabel(item.key, item.name)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex flex-col md:flex-row mt-auto gap-md shrink-0">
          <Button
            className="w-full"
            data-analytics-attributes={JSON.stringify({
              level,
              section: 'Auto Select Modal',
              text: 'Cancel selection'
            })}
            data-analytics-id="New Cars"
            data-testid="auto-select-cancel-button"
            onClick={onCancel}
            size="md"
            variant="tertiary"
          >
            Cancel selection
          </Button>
          <Button
            className="w-full"
            data-analytics-attributes={JSON.stringify({
              level,
              section: 'Auto Select Modal',
              text: 'Okay, looks good'
            })}
            data-analytics-id="New Cars"
            data-testid="auto-select-continue-button"
            onClick={onContinue}
            size="md"
            variant="primary"
          >
            Okay, looks good
          </Button>
        </div>
      </div>
    </DsModal>
  );
}
